import { createSelector } from 'reselect';
import memoize from 'transmute/memoize';
import { CopilotCreateForm, SubscriptionGroups, Pagination, CampaignBusinessUnit, SubmissionsExportName, Helpdesk, BusinessUnitUsers, OverridePaymentPermissions, ShareEmbedV3, SunsettingPopupCreation, AdConsentProperties, LimitedTicketFunctionInInbox, RemoveATCFromForms, MarketingHome, DefaultForm, DataEnrichmentBeta, PLGEmptyState, FormTemplates, NewEditorAccess, MarketingAnalyticsBeta, BusinessUnitPicker, HTMLSanitizion, InlineHtmlSanitization, LeadGenerationExperiment, NewEditorTicketProperties, RedirectToNewDashboardApp } from 'FormsUI/constants/Gates';
import { Set as ImmutableSet } from 'immutable';
import { getUserEmail } from 'FormsUI/selectors/User';
import { getQueryParams } from 'FormsUI/utils/QueryParams';
import { isHSUser } from 'FormsUI/utils/User';
export const getGates = ({
  gates = ImmutableSet()
}) => gates;
const isUngated = (...gateNames) => state => gateNames.filter(gateName => !getGates(state).has(gateName)).length === 0;
const createArray = value => {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};
/**
 * Helper that allows gates to be overridden with '?ungated=' (enabled)
 * and '?gated=' (disabled) query parameters.
 * Multiple gates can be overridden by repeating these parameters ('?ungated=...&ungated=...').
 * Available only to @hubspot.com users.
 *
 * WARNINGS:
 * - features that would be dangerous to expose to customers if the url leaked or was guessed, should not use this helper
 * - features which require the backend to be aware of the gate should not use this helper, as the override will only apply on the frontend
 * - "gated" or "ungated" will have priority over current gate configuration. Use it with caution!
 *
 * @example
 * https://hubspot[qa].com/path/to/page?ungated=MyCoolGateFeature (will enable the feature)
 * https://hubspot[qa].com/path/to/page?gated=MyCoolGateFeature (will disable the feature)
 */
const getGateOverride = memoize((gate, queryParams) => {
  const ungatedArr = createArray(queryParams.ungated);
  const gatedArr = createArray(queryParams.gated);
  return {
    isForceUngate: ungatedArr.includes(gate),
    isForceGate: gatedArr.includes(gate)
  };
});
export const createIsUngatedWithOverride = gate => createSelector(isUngated(gate), getUserEmail, (hasGate, userEmail) => {
  const queryParams = getQueryParams();
  const isHSUserEmail = isHSUser(userEmail);
  const {
    isForceGate,
    isForceUngate
  } = getGateOverride(gate, queryParams);
  if (isHSUserEmail && isForceUngate) {
    return true;
  } else if (isHSUserEmail && isForceGate) {
    return false;
  }
  return hasGate;
});
export const isUngatedForSubscriptionGroups = isUngated(SubscriptionGroups);
export const isUngatedForPagination = createIsUngatedWithOverride(Pagination);
export const isUngatedForCampaignBusinessUnit = createIsUngatedWithOverride(CampaignBusinessUnit);
export const isUngatedForSubmissionsExportName = isUngated(SubmissionsExportName);
export const isUngatedForHelpdesk = isUngated(Helpdesk);
export const isUngatedForLimitedTicketFunctionalityInInbox = isUngated(LimitedTicketFunctionInInbox);
export const isUngatedForRemoveATCFromForms = isUngated(RemoveATCFromForms);
export const isUngatedForOverridePaymentPermissions = isUngated(OverridePaymentPermissions);
export const isUngatedForBusinessUnitUsers = isUngated(BusinessUnitUsers);
export const isUngatedForShareEmbedV3 = isUngated(ShareEmbedV3);
export const isUngatedForSunsettingPopupCreation = isUngated(SunsettingPopupCreation);
export const isUngatedForNewEditorAccess = isUngated(NewEditorAccess);
export const isUngatedForAdConsentProperties = isUngated(AdConsentProperties);
export const isUngatedForBusinessUnitPicker = isUngated(BusinessUnitPicker);
export const isUngatedForMarketingHome = isUngated(MarketingHome);
export const isUngatedForDefaultForm = isUngated(DefaultForm);
export const isUngatedForDataEnrichmentBeta = isUngated(DataEnrichmentBeta);
export const isUngatedForMarketingAnalyticsBeta = isUngated(MarketingAnalyticsBeta);
export const isUngatedForPLGEmptyState = isUngated(PLGEmptyState);
export const isUngatedForFormTemplates = isUngated(FormTemplates);
export const isUngatedForHTMLSanitization = isUngated(HTMLSanitizion);
export const isUngatedForInlineHtmlSanitization = isUngated(InlineHtmlSanitization);
export const isUngatedForLeadGenerationExperiment = isUngated(LeadGenerationExperiment);
export const isUngatedForNewEditorTicketProperties = isUngated(NewEditorTicketProperties);
export const isUngatedForRedirectToNewDashboardApp = isUngated(RedirectToNewDashboardApp);
export const isUngatedForCopilotCreateForm = isUngated(CopilotCreateForm);